import { HeadingText } from '@paypalcorp/pp-react'
import React, { useEffect, useRef } from 'react'
import useTracking from '../../hooks/useTracking'
import { componentNames, fptiTypes } from '../../utils/analytics/fpti-constants'
import { ARTICLE_CONTENT_DATA } from '../../utils/prop-type-shape'

// styles
import './article-content.less'

// Article Component
function ArticleContent(props) {
  const { articleContent, articleId, exp } = props
  const { trackActivity, trackClick } = useTracking()
  const articleContentRef = useRef(null)

  // get the ECM API tags from article data
  const { ecmComponentIds, ecmDocId } = articleContent
  const helpDocId = ecmDocId || ''
  const helpComponentIds = ecmComponentIds || ''

  const handleClickOnLinks = (e, link) => {
    const href = link.getAttribute('href')

    if (!href) {
      e.preventDefault()
      trackClick({
        name: componentNames.ARTICLE_CONTENT,
        exp,
        type: fptiTypes.ON_CLICK,
        hpid: articleId,
        article_doc_id: articleId,
        help_doc_id: helpDocId,
        help_component_ids: helpComponentIds,
        link: `article-content|${articleId}|broken-link-clicked`,
      })
    } else {
      trackClick({
        name: componentNames.ARTICLE_CONTENT,
        exp,
        type: fptiTypes.ON_CLICK,
        article_doc_id: articleId,
        hpid: articleId,
        help_doc_id: helpDocId,
        help_component_ids: helpComponentIds,
        link: `article-content|${articleId}|link-clicked|${href}`,
      })
    }
  }

  // FPTI Events for video play and pause controls
  const addFPTIsForVideoPlayPause = (videoElement, videoSrc) => {
    videoElement.addEventListener('play', () => {
      // Add FPTI event for when video would be played
      trackClick({
        name: componentNames.ARTICLE_CONTENT,
        exp,
        link: `article-content-video-played|${videoSrc}`,
        hpid: articleId,
        help_doc_id: helpDocId,
        help_component_ids: helpComponentIds,
        event_name: 'article_content_video_played_in_article_page',
        type: fptiTypes.SUCCESS,
      })
    })

    videoElement.addEventListener('pause', () => {
      // Add FPTI event for when video would be paused
      trackClick({
        name: componentNames.ARTICLE_CONTENT,
        exp,
        link: `article-content-video-paused|${videoSrc}`,
        hpid: articleId,
        help_doc_id: helpDocId,
        help_component_ids: helpComponentIds,
        event_name: 'article_content_video_paused_in_article_page',
        type: fptiTypes.SUCCESS,
      })
    })
  }

  const parseArticleContent = () => {
    // Adding setTimeout on top as tag element is not picked immediately safari and IE versions
    setTimeout(() => {
      if (!articleContentRef) {
        return
      }

      // Link click Tracking in Article Component
      const contentLinks = articleContentRef.current.querySelectorAll('a[href]')
      Array.from(contentLinks).forEach((link) => {
        link.addEventListener('click', (e) => {
          const clickTarget = e.target
          const { id } = clickTarget

          // To check if the click content is solutionLink
          if (id && clickTarget.name === 'solutionLink') {
            // make the faqId the href for fpti usage
            link.href = id
          }

          handleClickOnLinks(e, link)
        })
      })

      // warp image in the article content
      let imageTags = articleContentRef.current.getElementsByTagName('img')
      imageTags = Array.prototype.slice.call(imageTags)
      imageTags.forEach((tag) => {
        const divEl = document.createElement('img')
        divEl.setAttribute('src', tag.src)
        divEl.style.height = `${tag.height}px`
        divEl.style.maxWidth = `${tag.width}px`
        divEl.setAttribute('alt', tag.alt)
        divEl.className = 'article-content__image'
        tag.parentNode.replaceChild(divEl, tag)
      })

      // Logic to add video event listeners for play and pause for the videos [Paypal object videos] that are inside iframe.
      let iframeTags = articleContentRef.current.getElementsByTagName('iframe')
      iframeTags = Array.prototype.slice.call(iframeTags)
      iframeTags.forEach((tag) => {
        const videoEl = tag
        videoEl.className = 'article-content__iFrame'
        videoEl.setAttribute(
          'src',
          tag.src && tag.src.replace('youtube.com', 'youtube-nocookie.com'),
        )
        tag.parentNode.replaceChild(videoEl, tag)

        videoEl.onload = function () {
          const iFrameDocument = videoEl.contentDocument
          const videoSrc = videoEl.src

          if (iFrameDocument) {
            const videoElement =
              iFrameDocument.getElementsByTagName('video')[0] || null
            if (videoElement) {
              // set video element width to 100% to display videos appropriatly for all device resolutions
              videoElement.setAttribute('width', '100%')

              trackActivity({
                name: componentNames.ARTICLE_CONTENT,
                exp,
                link: `article-content-video-displayed|${videoSrc}`,
                event_name: 'article_content_video_displayed_in_article_page',
                hpid: articleId,
                help_doc_id: helpDocId,
                help_component_ids: helpComponentIds,
                type: fptiTypes.SUCCESS,
              })

              addFPTIsForVideoPlayPause(videoElement, videoSrc)
            }
          }
        }
      })

      // Logic to add video event listeners for play and pause for the videos
      // that are coming directly with out iframe.
      let videoTags = articleContentRef.current.getElementsByTagName('video')
      videoTags = Array.prototype.slice.call(videoTags)

      videoTags.forEach((videoElement) => {
        const videoSrc = videoElement.currentSrc

        trackActivity({
          name: componentNames.ARTICLE_CONTENT,
          exp,
          link: `article-content-video-displayed|${videoSrc}`,
          event_name: 'article_content_video_displayed_in_article_page',
          hpid: articleId,
          help_doc_id: helpDocId,
          help_component_ids: helpComponentIds,
          type: fptiTypes.SUCCESS,
        })

        // FPTI event listeners for play and pause
        addFPTIsForVideoPlayPause(videoElement, videoSrc)
      })
    })
  }

  // On component mount
  useEffect(() => {
    if (!articleContent) {
      return trackActivity({
        name: componentNames.ARTICLE_CONTENT,
        exp,
        type: fptiTypes.ERROR,
        hpid: articleId,
        help_doc_id: helpDocId,
        help_component_ids: helpComponentIds,
      })
    }

    parseArticleContent()

    return trackActivity({
      name: componentNames.ARTICLE_CONTENT,
      exp,
      type: fptiTypes.SUCCESS,
      hpid: articleId,
      help_doc_id: helpDocId,
      help_component_ids: helpComponentIds,
    })
  }, [])

  return (
    <>
      <HeadingText
        data-testid="hc-article-content-title"
        className="article-content__heading"
        as="h2"
        size="sm"
      >
        {articleContent.articleTitle}
      </HeadingText>

      <div
        className="article-content__section"
        data-testid="hc-article-content-body"
        dangerouslySetInnerHTML={{
          __html: articleContent.articleContent,
        }}
        ref={articleContentRef}
      />
    </>
  )
}

ArticleContent.propTypes = ARTICLE_CONTENT_DATA

export default ArticleContent
